import { inject, injectable } from 'inversify';
import Resumable from 'resumablejs';
import { Observer, ReplaySubject, firstValueFrom, of, retry, throwError } from 'rxjs';

import { delay, mergeMap } from 'rxjs/operators';
import { DropboxFile } from '../models/dropbox-file';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../models/google-drive-file';
import { Logger } from '../models/logger';
import { UploadInProgressFile } from '../models/upload-in-progress-file';
import { GAContants } from '../types/ga.types';
import { OriginalInputFile, PSUploadTypes, WebsocketResponse } from '../types/types';
import { getFileMimeType } from '../utils/file';
import { get, isString, parseInt } from '../utils/lodash-min';
import { MetricErrorClass, errorClass, hostOnly, sizeBucket, statusOf } from '../utils/metric-attrs';
import { emitMetric } from '../utils/metric-emit';
import { MetricEventName } from '../types/metric-events';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { StringUtils } from '../utils/string-utils';
import { GAService } from './analytics.service';
import { AuthService } from './auth.service';
import { HelperService } from './helper.service';
import { HttpProgressEvent, LightHttp } from './light-http.service';
import { UploadOptions } from '../types/file-upload.types';
import { LoginService } from './login.service';
import { RefreshService } from './refresh.service';
import { SettingsService } from './settings.service';
import { UIContext } from './ui-context';
import { WebSocketService } from './websocket.service';



/** Closed set for the FILE_UPLOAD_* `kind` attribute (METRICS.md §4.2 `xc_upload`). */
export type UploadKind = 'local_single' | 'local_chunk' | 'url' | 'drive';

/**
 * Marker property set on the error thrown when the chunk-upload finalize GET
 * (`original-input-files`) answers with no data. Carried on the error rather
 * than parsed out of its message so the classification cannot drift when
 * someone rewords the message.
 */
export const FINALIZE_MISSING_MARKER = '__xcFinalizeMissing';

/**
 * IMPORTANT!: All files that are uploaded but be saved to fileUploadMap.<route_path> so that when WS recieves a message,
 * We can update corresponding FileUpload with the data.
 */
@injectable()
export class FileUploadService {
    logger = Logger.getLogger('FileUploadService');

    maxFileSizeInBytes = 600 * 1000 * 1000;
    maxFileSizeInMB = 600;
    concurrentProcessLimit = 4;
    chunkSize = 1 * 1024 * 1024 + 1;
    simultaneousUploads = 6;
    chunkUploadEnabled = true
    chunkSize1MB = 0;
    chunkSize10MB = 0;
    chunkSize100MB = 0;
    chunkSize500MB = 0;
    chunkSizeLargeFiles = 0;

    private _onFileUploadStart = new ReplaySubject<{ dispatchedAt: Date }>(1);
    get onFileUploadStart() {
        return this._onFileUploadStart;
    }

    private _onFileSelected = new ReplaySubject<{ dispatchedAt: Date }>(1);
    get onFileSelected() {
        return this._onFileSelected;
    }

    public fileSelected() {
        this._onFileSelected.next({ dispatchedAt: new Date() });
    }

    // private fileUploadMap: Map<UniqueProcessType, FileUpload[]> = new Map();
    // private fileUploadsChanged = new Subject<FileUpload>();
    // get onFileUploadChanged() {
    //     return this.fileUploadsChanged.asObservable();
    // }


    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(GAService) private gaService: GAService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(AuthService) private authService: AuthService,
        @inject(HelperService) private helperService: HelperService,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(LoginService) private loginService: LoginService,
        @inject(RefreshService) private refreshService: RefreshService,
    ) {

    }

    initialize() {
        this.loginService.onSessionDetails.subscribe({
            next: (sessionDetails) => {
                this.maxFileSizeInBytes = ((sessionDetails && sessionDetails.fileSizeLimitMB) || 400) * 1000 * 1000;
                this.maxFileSizeInMB = (sessionDetails && sessionDetails.fileSizeLimitMB) || 400;
                this.concurrentProcessLimit = (sessionDetails && sessionDetails.numberOfConcurrentProcessLimit) || 4;
            }
        });
        this.settingsService.userSettings.subscribe({
            next: (userSettings) => {
                this.chunkSize = get(userSettings, 'chunkSize', 40 * 1024 * 1024 + 1);
                this.chunkUploadEnabled = get(userSettings, 'chunkUploadEnabled', true);
                this.simultaneousUploads = get(userSettings, 'simultaneousUploads', 2);
                this.chunkSize1MB = get(userSettings, 'chunkSize1MB', 0);
                this.chunkSize10MB = get(userSettings, 'chunkSize10MB', 0);
                this.chunkSize100MB = get(userSettings, 'chunkSize100MB', 0);
                this.chunkSize500MB = get(userSettings, 'chunkSize500MB', 0);
                this.chunkSizeLargeFiles = get(userSettings, 'chunkSizeLargeFiles', 0);
            }
        });

        // this.fileManagerService.onFileUploadRemove()
        //     .subscribe((fileUpload) => {
        //         this.fileUploadMap.forEach(fileUploads => {
        //             const index = fileUploads.findIndex(f => f.id === fileUpload.id);
        //             if (index > -1) {
        //                 fileUploads.splice(index, 1);
        //             }
        //         });
        //         this.notify();
        //     });

        // this.fileManagerService.registerActiveFileUploadsProvider(this);
    }

    // public notify(fileUpload?: FileUpload) {
    //     this.fileUploadsChanged.next(fileUpload);
    // }

    // public getFileUploadArray(processType: UniqueProcessType) {
    //     if (!this.fileUploadMap.get(processType)) {
    //         this.fileUploadMap.set(processType, new Array());
    //     }
    //     return this.fileUploadMap.get(processType);
    // }

    // public getFileUploadMap() {
    //     return this.fileUploadMap;
    // }

    // public getFileUploads(): FileUpload[] {
    //     const fileArray = [];
    //     const fileMap: Map<UniqueProcessType, FileUpload[]> = this.getFileUploadMap();
    //     fileMap.forEach((fileUploads, uniqueProcessType) => {
    //         fileUploads.forEach(fileUpload => {
    //             fileArray.push(fileUpload);
    //         });
    //     });
    //     return fileArray;
    // }

    // public getFileUpload(_uuid: string): FileUpload {
    //     let item;
    //     this.fileUploadMap.forEach((fileUploads, key) => {
    //         fileUploads.forEach(fileUpload => {
    //             if (fileUpload.id === _uuid) {
    //                 item = fileUpload;
    //             }
    //         });
    //     });
    //     return item;
    // }

    private notifyFileUploadStart() {
        this._onFileUploadStart.next({ dispatchedAt: new Date() });
    }

    /**
     * FILE_UPLOAD_START / _COMPLETE / _ERROR.
     *
     * These three were defined in `MetricEventName` since March and never
     * emitted, which is why the 29 % of CONVERT_START sessions that never reach
     * FILE_DOWNLOAD has no explanation: an upload that dies before the convert
     * click leaves no trace at all today. Every attribute is bounded
     * (METRICS.md §3) — a size BUCKET not a byte count, the edge HOST not the
     * URL, an errorClass enum not the exception message.
     *
     * `edgeHost` deliberately matches the attribute THUMBNAIL_LOAD_FAILED
     * already carries, so "is one edge bad today" is a single query across both.
     */
    private emitUploadEvent(
        event: MetricEventName,
        base: { kind: UploadKind, size: number, endpoint: string, attempt?: number },
        extra: Record<string, string | number | boolean> = {},
    ) {
        emitMetric(event, {
            kind: base.kind,
            sizeBucket: sizeBucket(base.size),
            edgeHost: hostOnly(base.endpoint),
            retries: base.attempt || 0,
            ...extra,
        });
    }

    /**
     * FILE_UPLOAD_ERROR attributes for a thrown upload failure.
     *
     * `finalize_missing` is asserted by the chunk finalize path (see
     * `updateFileUploadFileOriginalInputFilesAndResolve`): the `original-input-files`
     * GET answering with no data after 3 retries is the measured east-32 prod
     * bug, and it is invisible in a status code because the request itself
     * succeeded. It is the reason this class exists rather than folding into
     * `unknown`.
     */
    private uploadErrorAttributes(err: any): { status: number, errorClass: MetricErrorClass } {
        const status = statusOf(err);
        const hint: MetricErrorClass | undefined = err?.[FINALIZE_MISSING_MARKER] ? 'finalize_missing' : undefined;
        return { status, errorClass: errorClass(status, hint, err?.message) };
    }

    public async uploadFileFromURL(
        fileWithMetadata: UploadInProgressFile<DropboxFile | GoogleDriveFile | File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const progressCallback = (_fileWithMetadata) => {
                let recievedSize = 0;
                const localFileWithMetadata: UploadInProgressFile<any> = _fileWithMetadata;
                return (data) => {
                    this.logger.trace7({ message: 'URL file upload progress response', data: data });
                    if (data.uuid === localFileWithMetadata.getFile().name) {
                        recievedSize = parseFloat(data.data) > recievedSize ? parseFloat(data.data) : recievedSize;
                        const progress = 100 * recievedSize / localFileWithMetadata.getFile().size;

                        if (recievedSize === localFileWithMetadata.getFile().size || parseInt(progress + '', 10) === 100) {
                            localFileWithMetadata.setComplete();
                        }

                        if (data.processStageSubState === 'completed') {
                            localFileWithMetadata.setComplete();
                            this.gaService.send(
                                GAContants.HitType.EVENT,
                                GAContants.EventCategory.NETWORK,
                                GAContants.EventAction['FILE-UPLOAD-END'],
                                GAContants.EventLabel.CONVERT,
                                `${localFileWithMetadata.metadata.file.name}`,
                            );
                            this.wsService.unregisterCallback(removableCallback);
                        }

                        if (!localFileWithMetadata.getComplete()) {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat(progress + '').toFixed(2)));
                        } else {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat('100').toFixed(2)));
                        }
                    }
                };
            };
            const removableCallback = progressCallback(fileWithMetadata);
            this.wsService.registerCallback(removableCallback);
            const urlUploadEndpoint = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + '/url';
            const metricBase = {
                kind: 'url' as UploadKind,
                size: fileWithMetadata.getFile().size,
                endpoint: urlUploadEndpoint,
                attempt: uploadOptions?.attempt,
            };
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase);
            let result: any;
            try {
                result = await firstValueFrom(this.http.post(urlUploadEndpoint, fileWithMetadata.getFile(), {
                    params: {
                        wsId: this.wsService.getId(),
                        wsDomain: this.wsService.getDomain(),
                        safeFilename: fileWithMetadata.metadata.processorMetadataList[0].fileMetadata.safeName,
                    },
                }));
            } catch (err) {
                this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                    ms: Date.now() - startedAt,
                    ...this.uploadErrorAttributes(err),
                });
                this.wsService.unregisterCallback(removableCallback);
                throw err;
            }
            // `ms` here is the edge's ACK of the fetch request, not the whole
            // byte transfer — that runs server-side and reports over the
            // websocket progress callback above. Named the same as the local
            // path on purpose: both answer "how long until the user's file is
            // accepted", which is the step the funnel loses people at.
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                ms: Date.now() - startedAt,
            });

            const info = {
                ftype: getFileMimeType(fileWithMetadata.getFile()),
                fname: fileWithMetadata.getFile().name,
                fsize: fileWithMetadata.getFile().size,
                flastModified: fileWithMetadata.getFile().lastModified,
            };
            const originalInputFiles = result.originalInputFiles[0];
            originalInputFiles.metadata = info;
            return originalInputFiles;
        }
        throw new Error(`Unsupported upload type for URL upload: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`);
    }

    public async uploadFileFromGoogleDrive(
        fileWithMetadata: UploadInProgressFile<File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const progressCallback = (_fileWithMetadata) => {
                let recievedSize = 0;
                const localFileWithMetadata: UploadInProgressFile<any> = _fileWithMetadata;
                return (data: WebsocketResponse) => {
                    this.logger.trace7({ message: 'GoogleDrive file upload progress response', data: data });
                    if (data.uuid === localFileWithMetadata.getFile().name) {
                        recievedSize = parseFloat(data.data) > recievedSize ? parseFloat(data.data) : recievedSize;
                        const progress = 100 * recievedSize / localFileWithMetadata.getFile().size;

                        if (recievedSize === localFileWithMetadata.getFile().size || parseInt(progress + '', 10) === 100) {
                            localFileWithMetadata.setComplete();
                        }

                        if (data.processStageSubState === 'completed') {
                            localFileWithMetadata.setComplete();
                            this.gaService.send(
                                GAContants.HitType.EVENT,
                                GAContants.EventCategory.NETWORK,
                                GAContants.EventAction['FILE-UPLOAD-END'],
                                GAContants.EventLabel.CONVERT,
                                `${localFileWithMetadata.metadata.file.name}`,
                            );
                            this.wsService.unregisterCallback(removableCallback);
                        }

                        if (!localFileWithMetadata.getComplete()) {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat(progress + '').toFixed(2)));
                        } else {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat('100').toFixed(2)));
                        }
                    }
                };
            };
            const removableCallback = progressCallback(fileWithMetadata);
            this.wsService.registerCallback(removableCallback);
            const url = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + '/google-drive';
            const metricBase = {
                kind: 'drive' as UploadKind,
                size: fileWithMetadata.getFile().size,
                endpoint: url,
                attempt: uploadOptions?.attempt,
            };
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase);
            let result: any;
            try {
                result = await firstValueFrom(this.http.post(url, fileWithMetadata.getFile(), {
                    params: {
                        wsId: this.wsService.getId(),
                        wsDomain: this.wsService.getDomain(),
                        safeFilename: fileWithMetadata.metadata.processorMetadataList[0].fileMetadata.safeName,
                    },
                }));
            } catch (err) {
                this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                    ms: Date.now() - startedAt,
                    ...this.uploadErrorAttributes(err),
                });
                this.wsService.unregisterCallback(removableCallback);
                throw err;
            }
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                ms: Date.now() - startedAt,
            });

            const info = {
                ftype: getFileMimeType(fileWithMetadata.getFile()),
                fname: fileWithMetadata.getFile().name,
                fsize: fileWithMetadata.getFile().size,
                flastModified: fileWithMetadata.getFile().lastModified,
            };
            const originalInputFiles = result.originalInputFiles[0];
            originalInputFiles.metadata = info;
            return originalInputFiles;
        }
        throw new Error(`Unsupported upload type for Google Drive upload: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`);
    }

    // This is a single file
    public async uploadFileFromLocalDisk(
        fileWithMetadata: UploadInProgressFile<File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        this.logger.trace7({ message: 'File upload from disk', data: uploadRequestMetadata });
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const endpointSuffix = this.chunkUploadEnabled ? "res" : "file"
            const fileUploadEndpoint = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + `/${endpointSuffix}`;
            const file = fileWithMetadata.getFile();
            const metricBase = {
                kind: (this.chunkUploadEnabled ? 'local_chunk' : 'local_single') as UploadKind,
                size: file.size,
                endpoint: fileUploadEndpoint,
                attempt: uploadOptions?.attempt,
            };
            // Chunk count is derived, not counted: getChunkSize() is
            // deterministic for a given size, so this is the same number
            // Resumable will produce and costs nothing to compute. Omitted
            // entirely on the single-request path (there are no chunks).
            const chunkExtra = this.chunkUploadEnabled
                ? { chunks: Math.max(1, Math.ceil(file.size / this.getChunkSize(file.size))) }
                : {};
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase, chunkExtra);
            return this.uploadSingleFile(fileUploadEndpoint, fileWithMetadata.getMetadata(), fileWithMetadata.getFile(), progressObserver)
                .then((uploadResponse: OriginalInputFile) => {
                    this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                        ...chunkExtra,
                        ms: Date.now() - startedAt,
                    });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.NETWORK,
                        GAContants.EventAction['FILE-UPLOAD-END'],
                        GAContants.EventLabel.CONVERT,
                        `${fileWithMetadata.metadata.file.name}`,
                    );
                    return uploadResponse;
                })
                .catch(err => {
                    this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                        ...chunkExtra,
                        ms: Date.now() - startedAt,
                        ...this.uploadErrorAttributes(err),
                    });
                    this.logger.error({ message: `Disk file upload failed due to ${err.message}`, error: err });
                    if (!uploadOptions?.suppressGAFailed) {
                        this.gaService.send(
                            GAContants.HitType.EVENT,
                            GAContants.EventCategory.MESSAGE,
                            GAContants.EventAction['FILE-UPLOAD-FAILED'],
                            GAContants.EventLabel.CONVERT,
                            '',
                        );
                    }
                    if (!uploadOptions?.suppressErrorDialog) {
                        this.uiContext.showErrorDialog({ title: 'Error', text: 'Could not update the file' });
                    }
                    throw err;
                });
        }
        // GCP/GCS upload path removed (cost). Uploads always go to the SERVER (ironman edge) path.
        throw new Error(
            `Unsupported upload type: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`,
        )
    }

    private uploadSingleFile(
        url: string, fileMetadata: FileUploadRequestMetadata, file: File, progressObserver: Observer<any>,
    ): Promise<any> {
        if (!this.chunkUploadEnabled) {
            return this.directUploadSingleFile(url, fileMetadata, file, progressObserver)
        }
        const fn = () => {
            return (resolve, reject) => {
                // HPY NOTE uploaded files should not come here, this service only to upload files
                const info = {
                    ftype: getFileMimeType(file),
                    fname: file.name,
                    fsize: file.size,
                    flastModified: file.lastModified,
                };
                this.chunkUploadSingleFile(url, fileMetadata, file, progressObserver)
                    .then(accuReply => {
                        resolve(Object.assign(accuReply.originalInputFiles[0], { metadata: info }));
                    }).catch(reject);
            };
        };
        return new Promise(fn());
    }

    private directUploadSingleFile(
        url: string,
        fileMetadata: FileUploadRequestMetadata,
        file: File,
        progressObserver: Observer<any>,
    ) {
        const safeFilename = fileMetadata.processorMetadataList[0].fileMetadata.safeName;
        const uploadUUID = StringUtils.uuid();
        const formData = new FormData();
        formData.append('files', file);

        const info = {
            ftype: getFileMimeType(file),
            fname: file.name,
            fsize: file.size,
            flastModified: file.lastModified,
        };

        return new Promise((resolve, reject) => {
            // Refresh a stale access token BEFORE getCommonHeaders() snapshots the
            // bearer below — otherwise a slow upload could send an expired token and
            // ironman would silently attribute it to ANONYMOUS (§6.3). Best-effort:
            // ensureFreshAccessToken never throws/blocks (flag-off/fresh/anon emits
            // synchronously, preserving today's timing). The header snapshot + send
            // happen inside the subscription so they run AFTER any refresh resolves.
            this.refreshService.ensureFreshAccessToken(url).subscribe(() => {
                const total = file.size
                const { response$, events$ } = this.http.request(
                    url,
                    'POST',
                    formData,
                    {
                        headers: this.http.getCommonHeaders(url),
                        params: {
                            safeName: safeFilename,
                            uuid: uploadUUID,
                        }
                    },

                );
                const eventsSub = events$.subscribe((event: HttpProgressEvent) => {
                    if (event.type === 'upload_progress') {
                        const progress = Math.round(100 * event.event.loaded / total);
                        progressObserver.next(progress);
                    }
                });
                response$.subscribe({
                    next: (accuReply: any) => {
                        eventsSub.unsubscribe();
                        resolve(Object.assign(accuReply.originalInputFiles[0], { metadata: info }));
                        progressObserver.complete()
                    },
                    error: (e) => {
                        eventsSub.unsubscribe();
                        reject(e)
                    }
                })
            });
        })

    }

    private getChunkSize(fileSize: number): number {
        const MB = 1024 * 1024;
        let tierOverride = 0;
        let hardcodedDefault = 0;

        if (fileSize < 1 * MB)        { tierOverride = this.chunkSize1MB;       hardcodedDefault = 100 * 1024; }
        else if (fileSize < 10 * MB)  { tierOverride = this.chunkSize10MB;      hardcodedDefault = 512 * 1024; }
        else if (fileSize < 100 * MB) { tierOverride = this.chunkSize100MB;     hardcodedDefault = 1 * MB; }
        else if (fileSize < 500 * MB) { tierOverride = this.chunkSize500MB;     hardcodedDefault = 2 * MB; }
        else                          { tierOverride = this.chunkSizeLargeFiles; hardcodedDefault = 5 * MB; }

        return (tierOverride || this.chunkSize || hardcodedDefault) + 1;
    }

    private chunkUploadSingleFile(
        url: string,
        fileMetadata: FileUploadRequestMetadata,
        file: File,
        progressObserver: Observer<any>,
    ): Promise<any> {
        const fn = () => {
            const safeFilename = fileMetadata.processorMetadataList[0].fileMetadata.safeName;
            return (resolve: (data) => void, reject: (error) => void) => {
                // Refresh a stale access token BEFORE Resumable's headers (built via
                // getCommonHeaders() below) snapshot the bearer — otherwise a slow
                // chunked upload could send an expired token and ironman would
                // silently attribute it to ANONYMOUS (§6.3). Best-effort: emits
                // synchronously when no refresh is needed (flag-off/fresh/anon),
                // preserving today's timing; the Resumable construction happens
                // inside the subscription so it runs AFTER any refresh resolves.
                this.refreshService.ensureFreshAccessToken(url).subscribe(() => {
                const uploadEndpoint = url;
                const accuReply = {
                    originalInputFiles: [],
                };
                const uploadUUID = StringUtils.uuid();
                let errorOccured = false;
                const r = new Resumable({
                    target: uploadEndpoint,
                    chunkSize: this.getChunkSize(file.size),
                    forceChunkSize: true,
                    simultaneousUploads: this.simultaneousUploads,
                    testChunks: false,
                    method: 'octet',
                    prioritizeFirstAndLastChunk: true,
                    maxChunkRetries: 5,
                    chunkRetryInterval: 3000,
                    ...{ permanentErrors: [400, 404, 406, 409, 415, 501] },
                    query: {
                        safeName: safeFilename,
                        uuid: uploadUUID,
                    },
                    maxFileSize: this.maxFileSizeInBytes as any,
                    // getCommonHeaders() is snapshotted ONCE for the whole file,
                    // so when the requestIdHeader flag is on every chunk of an
                    // upload carries the SAME X-Request-Id. That is the useful
                    // grouping — a chunked upload is one logical operation, and
                    // one id lets Graylog show all of its chunks together.
                    headers: this.http.getCommonHeaders(uploadEndpoint)
                });
                if (!r.support) {
                    alert('Your browser does not support this feature');
                    this.logger.error({ message: 'Your browser does not support this feature', data: { url, } });
                } // TODO: do better error handling

                r.on('fileProgress', (f, message) => {
                    const progress = r.progress();
                    progressObserver.next(parseFloat(parseFloat((progress * 100) + '').toFixed(2)));
                });

                r.on('fileSuccess', (f, message) => {
                    this.logger.trace7({
                        message: `Chunk file upload succcessfully ended. ${message}`,
                    });
                    try {
                        if (isString(message)) {
                            const parsed = JSON.parse(message);
                            if (parsed.originalInputFiles) {
                                accuReply.originalInputFiles.push(...parsed.originalInputFiles);
                            }
                            // If response is {"Uploaded":...} (non-completion chunk finished last), skip it
                        }
                    } catch (e) {
                        this.logger.fatal({
                            message: 'Error thrown while generating accuReply.originalInputFiles: ' + message + ` ${uploadUUID}`,
                            error: e,
                            data: { url, uploadUUID }
                        });
                        errorOccured = true;
                    }
                });

                r.on('complete', (f, message) => {
                    if (errorOccured || accuReply.originalInputFiles.length === 0) {
                        this.updateFileUploadFileOriginalInputFilesAndResolve(url, uploadUUID, safeFilename, accuReply, progressObserver, resolve, reject);
                    } else {
                        this.logger.trace7({
                            message: `Chunk file upload completed. ${message}`,
                        });
                        progressObserver.complete();
                        resolve(accuReply);
                    }
                });

                r.on('catchAll', (event, ...args) => {
                    //console.log(event, args);
                });

                r.on('cancel', () => {
                    //this.logger.log('cancel');
                });

                r.on('fileAdded', (f, event) => {
                    r.upload();
                });

                r.on('error', (e, rfile) => {
                    this.logger.error({ error: new Error(e), message: ` Error thrown by file uploader: ${e} ${uploadUUID}`, data: { uploadUUID } });
                    errorOccured = true;
                    reject(e);
                });

                r.addFile(file);
                });
            };
        };
        return new Promise(fn());
    }

    private updateFileUploadFileOriginalInputFilesAndResolve(url, uploadUUID, safeFilename, accuReply, progressObserver, resolve, reject) {
        this.http.get<{ [key: string]: [] }>(url + '/original-input-files', {
            params: {
                uuid: uploadUUID,
                safeName: safeFilename
            },
        }).pipe(
            mergeMap((resp) => {
                if (!resp || !resp.originalInputFiles) {
                    return of({}).pipe(
                        delay(2000),
                        mergeMap(() => {
                            return throwError(() => {
                                const error = new Error(`Request to get original uploaded file data returned null. ${uploadUUID} ${resp} ${url}`);
                                // The chunk-upload finalize answered 200 with no
                                // data ("Fallback GET found no data" on east-32,
                                // ironman xc_upload_finalize{outcome="missing"}).
                                // No status code can express it, so the class is
                                // carried on the error itself and read by
                                // uploadErrorAttributes() — otherwise a measured
                                // prod bug would be filed under `unknown`.
                                error[FINALIZE_MISSING_MARKER] = true;
                                return error;
                            });
                        }),
                    );
                }
                return of(resp)
            }),
            retry({ count: 3, delay: 2000 }),
        ).subscribe({
            next: (resp: any) => {
                this.logger.trace7({ message: `Chunk upload completed ${uploadUUID}`, data: { url, resp } });
                accuReply.originalInputFiles.push(...resp.originalInputFiles);
                progressObserver.complete();
                resolve(accuReply);
            },
            error: (err) => {
                reject(err);
                this.logger.error({ message: `Error file upload through chunk upload.${uploadUUID}`, error: err, data: { url, } });
                this.uiContext.showErrorDialog({ title: 'Upload failed', text: 'We could not upload your file. Please refresh the page and try again.' });
            },
        });
    }
}
